import { Check } from 'lucide-react';
import { clsx } from 'clsx';
import { Button, Callout, Select } from '@/shared/ui';
import patterns from '@/shared/ui/styles/patterns.module.css';
import type { ColumnMapping, ImportField, ImportFieldDef } from '../api/verificationApi';
import styles from './MapColumnsStep.module.css';

interface MapColumnsStepProps {
  fileName: string;
  rowCount: number;
  headers: string[];
  fields: ImportFieldDef[];
  mapping: ColumnMapping;
  onChange: (field: ImportField, header: string | null) => void;
  onBack: () => void;
  onContinue: () => void;
  saving: boolean;
  error: string | null;
}

const SKIP = '__skip__';

export function MapColumnsStep({
  fileName,
  rowCount,
  headers,
  fields,
  mapping,
  onChange,
  onBack,
  onContinue,
  saving,
  error,
}: MapColumnsStepProps) {
  const options = [
    { value: SKIP, label: '— skip —' },
    ...headers.map((h) => ({ value: h, label: h })),
  ];

  const missingRequired = fields.filter((f) => f.required && !mapping[f.key]);
  const canContinue = missingRequired.length === 0 && fields.length > 0;

  return (
    <div className={styles.root}>
      <Callout variant="accent" icon={Check}>
        {fileName} · {rowCount} rows detected · columns auto-matched below
      </Callout>

      <div className={clsx(patterns.panel, styles.table)}>
        <div className={styles.header}>
          <span>PracticeNova field</span>
          <span />
          <span>Your column</span>
        </div>
        {fields.map((f) => (
          <div key={f.key} className={styles.row}>
            <label htmlFor={`map-${f.key}`} className={styles.our}>
              {f.label}
              {f.required && (
                <span className={styles.star} aria-label="required">
                  {' '}
                  *
                </span>
              )}
            </label>
            <span className={styles.arrow} aria-hidden>
              →
            </span>
            <Select
              id={`map-${f.key}`}
              options={options}
              value={mapping[f.key] ?? SKIP}
              onChange={(e) => onChange(f.key, e.target.value === SKIP ? null : e.target.value)}
              fullWidth
            />
          </div>
        ))}
      </div>

      {error && <Callout variant="error">{error}</Callout>}
      {!error && missingRequired.length > 0 && (
        <p className={patterns.timestamp}>
          Still to map: {missingRequired.map((f) => f.label).join(', ')}.
        </p>
      )}

      <div className={styles.actions}>
        <Button variant="ghost" onClick={onBack} disabled={saving}>
          Back
        </Button>
        <Button onClick={onContinue} disabled={!canContinue} isLoading={saving}>
          Continue
        </Button>
      </div>
    </div>
  );
}
